import React, {useEffect, useState, useCallback} from 'react';
import axios from 'axios';
import "bootstrap/dist/css/bootstrap.min.css";
import 'bootstrap-icons/font/bootstrap-icons.css';
import { Toaster } from 'react-hot-toast';
import { motion } from 'framer-motion';
import Header from './components/Header';
import Footer from './components/Footer';
import Breadcrumb from './components/Breadcrumb';
import Search from './components/Search';
import RepoStructure from './components/RepoStructure';
import AppTypeForm from './components/AppTypeForm';
import SearchResults from "./components/SearchResults";
import PipelineStatus from './components/PipelineStatus';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Settings from './components/Settings';
import AppVersions from './components/AppVersions';
import usePipelineEvents from './hooks/usePipelineEvents';
import { getApiUrl, setLogoutCallback } from './utils/api';
import './App.css';

function App() {
    const octoAppType = "Octo+ App";
    const infraAppType = "Infra App";

    const [searchRepoStructure, setSearchRepoStructure] = useState([]);
    const [repoStructure, setRepoStructure] = useState([]);
         const [currentPath, setCurrentPath] = useState(null);
    const [appType, setAppType] = useState('');
    const [versionFields, setVersionFields] = useState({
        octoVersion: '',
        infraVersion: '',
        infraAppVersion: ''
    });
    const [otherEnvsVersion, setOtherEnvsVersion] = useState([]);
    const [currentEnvInfos, setCurrentEnvInfos] = useState({});
    const [envVersionsLoading, setEnvVersionsLoading] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [actionInProgress, setActionInProgress] = useState(false);
    const [pipelineState, setPipelineState] = useState("idle");
    const [searchRepoStructureLoading, setSearchRepoStructureLoading] = useState(true);
    const [currentPipelineId, setCurrentPipelineId] = useState(null);
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showAppVersions, setShowAppVersions] = useState(false);

    // Check authentication on app load
    useEffect(() => {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        
        if (token && userData) {
            try {
                const user = JSON.parse(userData);
                setUser(user);
                setIsAuthenticated(true);
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            } catch (error) {
                console.error('Error parsing user data:', error);
                handleLogout();
            }
        }
    }, []);

    // Set up logout callback for axios interceptor
    useEffect(() => {
        setLogoutCallback(handleLogout);
    }, []);

    // Real-time pipeline updates
    const handlePipelineUpdate = useCallback((pipelineId, updatedPipeline) => {
        // Update pipeline state if this is the current pipeline
        if (currentPipelineId === pipelineId) {
            if (updatedPipeline.status === 'running') {
                setPipelineState('loading');
            } else if (updatedPipeline.status === 'success') {
                setPipelineState('success');
            } else if (updatedPipeline.status === 'failed' || updatedPipeline.status === 'stopped') {
                setPipelineState('failed');
            }
        }
    }, [currentPipelineId]);

    usePipelineEvents(handlePipelineUpdate);

    // Handle login
    const handleLogin = (userData) => {
        setUser(userData);
        setIsAuthenticated(true);
    };

    // Handle logout
    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        setIsAuthenticated(false);
        setCurrentPath(null);
        setShowSettings(false);
        setShowAppVersions(false);
        setRepoStructure([]);
        setAppType('');
        setCurrentPipelineId(null);
        setActionInProgress(false);
        setPipelineState("idle");
        setSearchLoading(false);
        setSearchRepoStructureLoading(false);
        setEnvVersionsLoading(false);
    };

    // Handle navigation from dashboard
    const handleNavigateToApp = (appPath = '') => {
        // Navigate to specific app path or root to show available typologies
        setCurrentPath(appPath);
        setShowSettings(false);
        setShowAppVersions(false);
    };

    // Handle navigation back to dashboard
    const handleNavigateToDashboard = () => {
        setCurrentPath(null);
        setShowSettings(false);
        setShowAppVersions(false);
    };

    // Handle settings navigation
    const handleNavigateToSettings = () => {
        setShowSettings(true);
        setCurrentPath(null);
        setShowAppVersions(false);
    };

    // Handle app versions navigation
    const handleNavigateToAppVersions = () => {
        setShowAppVersions(true);
        setCurrentPath(null);
        setShowSettings(false);
    };

    useEffect(() => {
        if (!isAuthenticated) return;
        
        async function fetchRepoStructure(path = '') {
            setSearchRepoStructureLoading(true);
            try {
                const {data} = await axios.get(getApiUrl(`/api/repo-structure?path=${path}`));
                console.log('Repo structure response:', data);
                
                // Always set the repo structure first, so navigation works
                setRepoStructure(data.values || []);
                
                const argocdappFile = data.values?.filter(x => x.path.includes('argocd-app'))[0];
                if (argocdappFile) {
                    const {data: fileContent} = await axios.get(getApiUrl(`/api/file-content?path=${argocdappFile.path}`));
                    if (fileContent.includes('helm/octo')) {
                        setAppType(octoAppType);
                        setEnvVersionsLoading(true);
                        setCurrentEnvInfos({});
                        setOtherEnvsVersion([]);
                        const {data: currentEnvInfos} = await axios.get(getApiUrl(`/api/environment/infos?path=${path}`));
                        console.log(currentEnvInfos);
                        setCurrentEnvInfos(currentEnvInfos);

                        const {data: otherEnvsVersions} = await axios.get(getApiUrl(`/api/search/environments/version?path=${path}`));
                        setOtherEnvsVersion(otherEnvsVersions);

                        setEnvVersionsLoading(false);
                    } else {
                        setAppType(infraAppType);
                    }
                } else {
                    setAppType('');
                }
            } catch (error) {
                console.error('Error fetching repo structure:', error);
            }
            setSearchRepoStructureLoading(false);
            setEnvVersionsLoading(false);
        }

        fetchRepoStructure(currentPath);
    }, [currentPath, isAuthenticated]);

    const navigateToFolder = (path) => {
        setSearchRepoStructure([]);
        setCurrentPath(path);
        setPipelineState("idle");
        setCurrentPipelineId(null);
    };

    const goBack = () => {
        const pathParts = currentPath.split('/');
        pathParts.pop();
        setCurrentPath(pathParts.join('/'));
        setPipelineState("idle");
        setCurrentPipelineId(null);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleSearch = async () => {
        try {
            setSearchLoading(true);
            setSearchRepoStructure([]);
            const {data} = await axios.get(getApiUrl(`/api/search?searchTerm=${searchTerm}`));
            setSearchLoading(false);
            setSearchRepoStructure(data.values);
        } catch (error) {
            console.error(error);
        }
    };

    const closeSearch = async () => {
        try {
            setSearchRepoStructure([]);
        } catch (error) {
            console.error(error);
        }
    };

    const triggerUpdateAppPipeline = async () => {
        if (!currentPath || (!versionFields.octoVersion && !versionFields.infraVersion && !versionFields.infraAppVersion)) {
            alert('Please navigate to a directory and provide required versions before triggering the pipeline.');
            return;
        }
        const confirmMessage = `Are you sure you want to trigger the pipeline?
          Path: ${currentPath}
          ${appType === octoAppType ? `Octo Version: ${versionFields.octoVersion}\nInfra Version: ${versionFields.infraVersion}` : `Infra App Version: ${versionFields.infraAppVersion}`}`;

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setPipelineState("loading");

        try {
            await axios.post(getApiUrl('/api/mute/app'), {service: currentEnvInfos.service, namespace: currentEnvInfos.namespace, cluster: currentEnvInfos.cluster});
            const apiEndpoint = appType === octoAppType ? "octo-app-update-pipeline" : "infra-app-update-pipeline";
            const {data} = await axios.post(getApiUrl(`/api/${apiEndpoint}`), {
                selectedDirectory: currentPath,
                ...versionFields,
            });
            setCurrentPipelineId(data.uuid);
            setPipelineState("loading");
        } catch (error) {
            console.error(error);
            alert("Error triggering pipeline.");
            setPipelineState("failed");
        }
    };

    const triggerOctoAppStopPipeline = async () => {
        try {
            setActionInProgress(true);
            const {data} = await axios.post(getApiUrl('/api/octo-app-stop-pipeline'), {selectedDirectory: currentPath});
            setCurrentPipelineId(data.uuid);
            setActionInProgress(false);
            window.open(data.url, "_blank");
        } catch (error) {
            console.error(error);
            alert("Error triggering pipeline.");
        }
    };

    const triggerOctoAppStartPipeline = async () => {
        try {
            setActionInProgress(true);
            const {data} = await axios.post(getApiUrl('/api/octo-app-start-pipeline'), {selectedDirectory: currentPath});
            setCurrentPipelineId(data.uuid);
            setActionInProgress(false);
            window.open(data.url, "_blank");
        } catch (error) {
            console.error(error);
            alert("Error triggering pipeline.");
        }
    };

    const triggerOctoAppRestartPipeline = async () => {
        try {
            setActionInProgress(true);
            const {data} = await axios.post(getApiUrl('/api/octo-app-restart-pipeline'), {selectedDirectory: currentPath});
            setCurrentPipelineId(data.uuid);
            setActionInProgress(false);
            window.open(data.url, "_blank");
        } catch (error) {
            console.error(error);
            alert("Error triggering pipeline.");
        }
    };

    const triggerArgoAppUpdatePipeline = async () => {
        try {
            setActionInProgress(true);
            const {data} = await axios.post(getApiUrl('/api/argo-app-update-pipeline'), {selectedDirectory: currentPath});
            setCurrentPipelineId(data.uuid);
            setActionInProgress(false);
        } catch (error) {
            console.error(error);
            alert("Error triggering pipeline.");
        }
    };

    const muteAppAlerts = async () => {
        try {
            setActionInProgress(true);
            await axios.post(getApiUrl('/api/mute/app'), {service: currentEnvInfos.service, namespace: currentEnvInfos.namespace, cluster: currentEnvInfos.cluster});
            setActionInProgress(false);
        } catch (error) {
            console.error(error);
            setActionInProgress(false);
            alert("Error muting alerts.");
        }
    };

    const muteClusterAlerts = async (clusterName) => {
        try {
            setActionInProgress(true);
            await axios.post(getApiUrl('/api/mute/cluster'), {clusterName: clusterName});
            setActionInProgress(false);
            alert("Alerts muted successfully for the cluster " + clusterName);
        } catch (error) {
            console.error(error);
            setActionInProgress(false);
            alert("Error muting alerts.");
        }
    };

    const handleVersionChange = (field, value) => {
        setVersionFields(prev => ({...prev, [field]: value}));
    };

    const subtitle = () => {
        const nbFolders = currentPath ? currentPath.split("/").length : 0;
        const forWhat = currentPath.split('/').pop();
        const isClusterPage = nbFolders === 2;
        return (
            <div className="d-inline-flex gap-2 align-items-baseline">
                <h2 className="h5">
                    {(nbFolders === 0 ? "Choose a Typology" : (nbFolders === 1 ? "Choose a Cluster for type " : "Choose an APP for cluster "))}
                </h2>
                <h2 className="text-primary h5 fw-bold">{forWhat}</h2>
            </div>
        );
    };
    
    const logo = () => {
        return (
            <i className="bi bi-folder-fill me-2"></i>
        );
    };

    // Show login if not authenticated
    if (!isAuthenticated) {
        return (
            <>
                <Login onLogin={handleLogin} />
                <Toaster 
                    position="top-right"
                    toastOptions={{
                        duration: 4000,
                        style: {
                            background: 'rgba(255, 255, 255, 0.95)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '12px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        },
                    }}
                />
            </>
        );
    }

    return (
        <>
            <Header user={user} onLogout={handleLogout} onNavigateToApp={handleNavigateToApp}/>
            <motion.div 
                className="main-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                                 <div className="h-100 mb-5 pe-5 ps-5 pt-3 overflow-auto">
                     {/* Show Dashboard on home page (no path selected and not in settings/app versions) */}
                     {currentPath === null && !showSettings && !showAppVersions && (
                         <Dashboard 
                             onNavigateToApp={handleNavigateToApp} 
                             onNavigateToSettings={handleNavigateToSettings}
                             onNavigateToAppVersions={handleNavigateToAppVersions}
                         />
                     )}

                     {/* Show Settings page */}
                     {showSettings && (
                         <Settings onNavigateToDashboard={handleNavigateToDashboard} />
                     )}

                     {/* Show App Versions page */}
                     {showAppVersions && (
                         <AppVersions onNavigateToDashboard={handleNavigateToDashboard} />
                     )}
 
                     {/* Show navigation and content when path is selected */}
                     {(currentPath !== null && !showSettings) && (
                        <>
                            <motion.div 
                                className="d-inline-flex mb-3 justify-content-between w-100"
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 }}
                            >
                                <Breadcrumb currentPath={currentPath} setCurrentPath={setCurrentPath}/>
                            </motion.div>
                            
                            {!appType && (
                                <RepoStructure
                                    searchRepoStructureLoading={searchRepoStructureLoading}
                                    repoStructure={repoStructure}
                                    currentPath={currentPath}
                                    navigateToFolder={navigateToFolder}
                                    goBack={goBack}
                                    subtitle={subtitle}
                                    logo={logo}
                                    muteClusterAlerts={muteClusterAlerts}
                                />
                            )}
                            
                            {appType && (
                                <>
                                    <PipelineStatus 
                                        currentPath={currentPath}
                                        pipelineId={currentPipelineId}
                                    />
                                    <AppTypeForm
                                        appType={appType}
                                        currentPath={currentPath}
                                        versionFields={versionFields}
                                        handleVersionChange={handleVersionChange}
                                        triggerUpdateAppPipeline={triggerUpdateAppPipeline}
                                        triggerOctoAppStopPipeline={triggerOctoAppStopPipeline}
                                        triggerOctoAppStartPipeline={triggerOctoAppStartPipeline}
                                        triggerOctoAppRestartPipeline={triggerOctoAppRestartPipeline}
                                        triggerArgoAppUpdatePipeline={triggerArgoAppUpdatePipeline}
                                        muteAppAlerts={muteAppAlerts}
                                        actionInProgress={actionInProgress}
                                        pipelineState={pipelineState}
                                        goBack={goBack}
                                        otherEnvsVersion={otherEnvsVersion}
                                        envVersionsLoading={envVersionsLoading}
                                        currentEnvInfos={currentEnvInfos}
                                    />
                                </>
                            )}
                        </>
                    )}
                </div>
            </motion.div>
            <Toaster 
                position="top-right"
                toastOptions={{
                    duration: 4000,
                    style: {
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '12px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    },
                }}
            />
        </>
    );
}

export default App;
